import { useEffect, useId, useRef, useState, type MouseEvent } from 'react'
import { useRouter } from 'next/router'
import SearchOutlined from '@mui/icons-material/SearchOutlined'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import HomeOutlined from '@mui/icons-material/HomeOutlined'
import PersonOutline from '@mui/icons-material/PersonOutline'
import FavoriteBorder from '@mui/icons-material/FavoriteBorder'
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined'
import Tune from '@mui/icons-material/Tune'
import Link from 'src/components/Link'
import { useDispatch } from 'src/redux/hooks'
import { hrefForNavigationItem } from 'src/functions/navigationBreadcrumbs'
import { getSearchImageProps } from 'src/layout/Header/searchImages'
import type { item, tMenu } from 'src/shared/frontendTypes/navigation'
import NavigationRedux from './Navigation.Redux'
import DrawerRedux from './Drawer.Redux'
import ModernFilterPanel from './ModernFilterPanel'
import styles from './ModernNavigation.module.css'

interface Props {
  paths: tMenu
  activeMenuId: number | null
  activeBrandId: number | null
}

const shortcuts = [
  { title: 'Home', href: '/', Icon: HomeOutlined },
  { title: 'Kundenkonto', href: '/kundenkonto', Icon: PersonOutline },
  { title: 'Wunschliste', href: '/wunschliste', Icon: FavoriteBorder },
]

const ModernNavigation = ({ paths, activeMenuId, activeBrandId }: Props) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const { toggle: closeDrawer } = DrawerRedux()
  const { displayFilter, setDisplayFilter, resetFilter } = NavigationRedux()
  // One open branch per level keeps even a deep catalog easy to scan.
  const [expanded, setExpanded] = useState<number[]>([])
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const previouslyShowingFilter = useRef(displayFilter)
  const menuId = useId()
  const canFilter = router.pathname === '/suche' || /^\/(m|b)\//.test(router.pathname)

  useEffect(() => {
    if (previouslyShowingFilter.current && !displayFilter) {
      filterButtonRef.current?.focus({ preventScroll: true })
    }
    previouslyShowingFilter.current = displayFilter
  }, [displayFilter])

  const navigate = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
    if (router.asPath.split(/[?#]/)[0] !== href.split(/[?#]/)[0]) resetFilter()
    setDisplayFilter(false)
    closeDrawer(event)
  }

  const renderItems = (items: tMenu, depth = 0, parentBrand: item | null = null) => (
    <ul className={depth === 0 ? styles.categories : styles.children}>
      {items.map(entry => {
        const href = hrefForNavigationItem(entry, parentBrand)
        const hasChildren = Boolean(entry.children?.length)
        const open = expanded[depth] === entry.id
        const panelId = `${menuId}-category-${entry.id}`
        const selected = Boolean(
          (activeMenuId && entry.menueId === activeMenuId
            && (!activeBrandId || parentBrand?.brand === activeBrandId))
          || (!activeMenuId && activeBrandId && entry.brand === activeBrandId)
        )
        const rowClass = depth === 0 ? styles.card : styles.subcategory
        const label = <>
          {depth === 0 && entry.imageSrc && <img
            {...getSearchImageProps(entry.imageSrc)}
            sizes='(max-width: 420px) calc((100vw - 32px) * .78), 303px'
            className={styles.image} alt='' loading='lazy' decoding='async'
            onError={event => {
              const image = event.currentTarget
              if (image.getAttribute('src') !== entry.imageSrc) {
                image.removeAttribute('srcset')
                image.src = entry.imageSrc || ''
              } else image.style.visibility = 'hidden'
            }}
          />}
          {depth === 0 && !entry.imageSrc && entry.title === 'Marken' &&
            <LocalOfferOutlined className={styles.brandIllustration} />}
          <span className={styles.title}>{entry.title}</span>
          {hasChildren ? <ExpandMore className={`${styles.arrow} ${open ? styles.expanded : ''}`} />
            : <ChevronRight className={styles.arrow} />}
        </>

        return <li key={entry.id} className={depth === 0 ? styles.category : undefined}>
          {hasChildren ? <button
            type='button' className={rowClass} aria-expanded={open} aria-controls={panelId}
            onClick={() => setExpanded(current => open
              ? current.slice(0, depth)
              : [...current.slice(0, depth), entry.id])}
          >{label}</button> : href ? <Link
            href={href} className={rowClass} aria-current={selected ? 'page' : undefined}
            onClick={event => navigate(event, href)}
          >{label}</Link> : <span className={rowClass}>{label}</span>}

          {hasChildren && <div id={panelId} hidden={!open}>
            {open && <>
              {href && <Link href={href} className={styles.viewAll}
                aria-current={selected ? 'page' : undefined}
                onClick={event => navigate(event, href)}
              >{entry.title === 'Marken' ? 'Alle Marken entdecken' : `Alles in ${entry.title}`}<ChevronRight /></Link>}
              {renderItems(entry.children || [], depth + 1, entry.brand ? entry : parentBrand)}
            </>}
          </div>}
        </li>
      })}
    </ul>
  )

  if (displayFilter && canFilter) return <ModernFilterPanel />

  return <div className={styles.root} data-mobile-menu='modern'>
    <div className={styles.tools}>
      <button type='button' className={styles.search} onClick={event => {
        closeDrawer(event)
        dispatch({ type: 'mobileSucheToggle', payload: true })
      }}><SearchOutlined /><span>Wonach suchst du?</span></button>
      {canFilter && <button ref={filterButtonRef} type='button' className={styles.filter} onClick={() => setDisplayFilter(true)}>
        <Tune /><span>Diese Auswahl filtern</span><ChevronRight />
      </button>}
    </div>
    <nav className={styles.scroll} aria-label='Kategorien'>
      {renderItems(paths)}
    </nav>
    <nav className={styles.shortcuts} aria-label='Persönlicher Bereich'>
      {shortcuts.map(({ title, href, Icon }) => <Link key={href} href={href} onClick={closeDrawer}>
        <Icon /><span>{title}</span>
      </Link>)}
    </nav>
  </div>
}

export default ModernNavigation
